import React, { useCallback, useEffect, useMemo } from 'react'

import type { InfiniteData } from '@tanstack/react-query'
import { format } from 'date-fns'
import { Controller, type FieldErrors, useForm } from 'react-hook-form'
import { Control } from 'react-hook-form'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'

import { Badge } from '@/components/common/BadgeV2'
import Button from '@/components/common/Button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog'
import { Skeleton } from '@/components/common/Skeleton'
import Spinner from '@/components/common/Spinner'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import ErrorMessage from '@/components/form/ErrorMessage'
import Label from '@/components/form/Label'
import { SalesforceIcon } from '@/components/integrations/Salesforce/Icon'
import { SalesforceFieldInput } from '@/components/integrations/Salesforce/SalesforceFieldInput'
import { EMPTY_ESTIMATED_VALUE } from '@/components/pursuits/shared/renderPursuitField'

import {
  formatDollarAmount,
  formatDollarAmountWithSuffix,
} from '@/helpers/string'

import {
  useLatestPursuitSchema,
  useLatestPursuitSchemaId,
} from '@/hooks/pursuits/schemas/usePursuitSchemas'
import { useCreatePursuit } from '@/hooks/pursuits/useCreatePursuit'
import { useCreateSalesforceOpportunity } from '@/hooks/pursuits/useCreateSalesforceOpportunity'
import { usePursuit } from '@/hooks/pursuits/usePursuit'
import { usePursuitStages } from '@/hooks/pursuits/usePursuitStages'
import type {
  SalesforceField,
  SalesforceFieldType,
} from '@/hooks/salesforce/types'
import { useSalesforceIntegration } from '@/hooks/salesforce/useSalesforceIntegration'
import { useSalesforceOpportunityFields } from '@/hooks/salesforce/useSalesforceOpportunityFields'

import type { PaginatedResponse } from '@/types/Api'
import type {
  ExtendedSchemaDerivedField,
  ExtendedSchemaFieldType,
} from '@/types/extended-schema'
import type { UUID } from '@/types/Id'
import type { CreatePursuit, Pursuit } from '@/types/pursuit/Pursuit'
import type { PursuitStage } from '@/types/pursuit/Stage'

type SalesforceValue = string | number | boolean | null

export interface ManualSyncDialogFormValues {
  [key: string]: SalesforceValue
}

const IGNORED_FIELDS: string[] = [
  'Name',
  'StageName',
  'CloseDate',
  'Description',
]
const IGNORE_TYPES: SalesforceFieldType[] = ['reference']

interface FromPursuit {
  type: 'pursuit'
  pursuitId: UUID
}

interface FromCreatePursuit {
  type: 'create_pursuit'
  create_pursuit_req: CreatePursuit
}

interface ManualSyncDialogProps {
  orgId: UUID
  open: boolean
  from: FromPursuit | FromCreatePursuit
  onOpenChange: (open: boolean) => void
  onCreatePursuitSuccess?: (pursuit: Pursuit) => void
}

interface FieldRendererProps {
  field: SalesforceField
  control: Control<ManualSyncDialogFormValues>
  errors: FieldErrors<ManualSyncDialogFormValues>
  prefillSource?: 'override' | 'pursuit'
}

interface DefaultFieldProps {
  children: React.ReactNode
  className?: string
}

const DefaultField: React.FC<DefaultFieldProps> = ({ children, className }) => {
  return (
    <span
      className={twMerge(
        'text-sm text-primary-700 dark:text-primary-200',
        className,
      )}
    >
      {children}
    </span>
  )
}

const validateBoolean = (v: unknown) => {
  if (typeof v === 'boolean') return true
  return 'Required'
}

const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  control,
  errors,
  prefillSource,
}) => {
  const renderField = useCallback(() => {
    if (field.type === 'reference') {
      return null
    }
    const rules =
      field.type === 'boolean'
        ? {
            // For booleans, false should still be valid when required.
            // Validate presence (true or false), not truthiness.
            // Return a string to show an error message when missing.
            validate: validateBoolean,
          }
        : { required: !field.nillable }
    return (
      <Controller
        name={field.name}
        control={control}
        rules={rules}
        render={({ field: controllerField }) => (
          <div>
            <SalesforceFieldInput
              field={field}
              value={controllerField.value}
              onChange={controllerField.onChange}
              error={errors[field.name]?.message}
            />
            {errors[field.name] && (
              <ErrorMessage className="mt-1">
                {errors[field.name]?.message}
              </ErrorMessage>
            )}
          </div>
        )}
      />
    )
  }, [field, control, errors])

  return (
    <div className="space-y-2">
      <Label htmlFor={field.name} required={!field.nillable}>
        <span className="inline-flex items-center gap-2.5">
          <span>{field.label}</span>
          {prefillSource && (
            <Tooltip delayDuration={0}>
              <TooltipTrigger type="button">
                <Badge variant="outline" className="text-xs px-1 py-0.5">
                  Mapped
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="max-w-[260px]" side="right">
                {prefillSource === 'override'
                  ? 'Mapped from Salesforce Opportunity Field Mapping (Static Override)'
                  : 'Mapped from Salesforce Opportunity Field Mapping (Pursuit Field)'}
              </TooltipContent>
            </Tooltip>
          )}
        </span>
      </Label>
      {renderField()}
    </div>
  )
}

interface BaseHeaderProps {
  title: string
  description: string | null
  stage: string | null
  closeDate: string
  estimatedValue: number | null
}

const BaseHeader: React.FC<BaseHeaderProps> = ({
  title,
  description,
  stage,
  closeDate,
  estimatedValue,
}) => {
  if (!title) {
    return (
      <div className="flex justify-center items-center h-[300px]">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-2 mb-2">
      <div className="space-y-4">
        <div className="flex flex-col gap-y-1">
          <Label>Name</Label>
          <DefaultField>{title}</DefaultField>
        </div>
        <div className="flex flex-col gap-y-1">
          <Label>Description</Label>
          {description ? (
            <DefaultField>{description}</DefaultField>
          ) : (
            <DefaultField className="text-primary-500 dark:text-primary-400">
              No description
            </DefaultField>
          )}
        </div>
        <div className="flex items-center gap-x-4">
          {stage && (
            <div className="flex flex-col gap-y-1 w-1/2">
              <Label>Stage</Label>
              <DefaultField>{stage}</DefaultField>
            </div>
          )}
          <div className="flex flex-col gap-y-1 w-1/2">
            <Label>Close Date</Label>
            <DefaultField>{closeDate}</DefaultField>
          </div>
          <div className="flex flex-col gap-y-1 w-1/2">
            <Label>Estimated Value</Label>
            {estimatedValue ? (
              <DefaultField>
                {formatDollarAmountWithSuffix(estimatedValue, 2)}
              </DefaultField>
            ) : (
              <DefaultField className="text-primary-500 dark:text-primary-400">
                {EMPTY_ESTIMATED_VALUE}
              </DefaultField>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

interface FromPursuitHeaderProps {
  pursuitId: UUID
}

const FromPursuitHeader: React.FC<FromPursuitHeaderProps> = ({ pursuitId }) => {
  const { data: pursuit } = usePursuit({ pursuitId })
  if (!pursuit) {
    return (
      <div className="flex justify-center items-center">
        <div className="flex flex-col gap-y-[10px] w-full">
          <Skeleton className="h-[42px] w-full" />
          <Skeleton className="h-[42px] w-full" />
          <Skeleton className="h-[42px] w-full" />
        </div>
      </div>
    )
  }
  return (
    <BaseHeader
      title={pursuit.title}
      description={pursuit.description}
      stage={pursuit.stage.name}
      closeDate={format(pursuit.due_date, 'MMMM do, yyyy')}
      estimatedValue={pursuit.estimated_value}
    />
  )
}

interface FromCreatePursuitHeaderProps {
  orgId: UUID
  createPursuitReq: CreatePursuit
}

const FromCreatePursuitHeader: React.FC<FromCreatePursuitHeaderProps> = ({
  orgId,
  createPursuitReq,
}) => {
  const selectStage = useCallback(
    (stages: PursuitStage[]) =>
      stages.find((stage) => stage.id === createPursuitReq.stage_id),
    [createPursuitReq.stage_id],
  )
  const { data: stage } = usePursuitStages({ orgId, selector: selectStage })
  return (
    <BaseHeader
      title={createPursuitReq.title}
      description={createPursuitReq.description}
      stage={stage?.name ?? null}
      closeDate={format(createPursuitReq.due_date, 'MMMM do, yyyy')}
      estimatedValue={createPursuitReq.estimated_value}
    />
  )
}

const getValidFields = (fields: SalesforceField[]) => {
  return fields
    .filter(
      (field) =>
        field.createable &&
        !IGNORED_FIELDS.includes(field.name) &&
        !IGNORE_TYPES.includes(field.type),
    )
    .sort((a, b) => a.label.localeCompare(b.label))
}

// --- Value coercion between Sweetspot extended schema types and Salesforce field types ---
type SSValue =
  | string
  | number
  | boolean
  | null
  | string[]
  | number[]
  | undefined

function toStringValue(v: SSValue): string | null {
  if (v === null || v === undefined) return null
  if (Array.isArray(v))
    return v.map((x) => (x == null ? '' : String(x))).join(', ')
  return String(v)
}

function toMultiPicklistString(v: SSValue): string | null {
  if (v === null || v === undefined) return null
  if (Array.isArray(v))
    return v.map((x) => (x == null ? '' : String(x))).join(';')
  return String(v)
}

function toBooleanValue(v: SSValue): boolean | null {
  if (v === null || v === undefined) return null
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v !== 0
  const s = String(v).trim().toLowerCase()
  if (['true', '1', 'yes', 'y', 'on'].includes(s)) return true
  if (['false', '0', 'no', 'n', 'off'].includes(s)) return false
  return null
}

function toNumberValue(v: SSValue): number | null {
  if (v === null || v === undefined) return null
  if (typeof v === 'number') return v
  if (Array.isArray(v)) {
    const first = v.find((x) => x !== null && x !== undefined)
    if (first === undefined) return null
    const n = Number(first)
    return Number.isFinite(n) ? n : null
  }
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function toIntValue(v: SSValue): number | null {
  const n = toNumberValue(v)
  return n === null ? null : Math.trunc(n)
}

function toDateOnlyString(v: SSValue): string | null {
  if (v === null || v === undefined) return null
  const s = String(v)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  const d = new Date(s)
  if (isNaN(d.getTime())) return null
  return format(d, 'yyyy-MM-dd')
}

function toDateTimeISO(v: SSValue): string | null {
  if (v === null || v === undefined) return null
  const d = new Date(String(v))
  if (isNaN(d.getTime())) return null
  return d.toISOString()
}

function coerceToSalesforceValue(
  sfField: SalesforceField,
  ssVal: SSValue,
  _ssType?: ExtendedSchemaFieldType,
): SalesforceValue {
  switch (sfField.type) {
    case 'string':
    case 'textarea':
    case 'id':
    case 'email':
    case 'phone':
    case 'url':
      return toStringValue(ssVal)
    case 'picklist':
      return toStringValue(ssVal)
    case 'multipicklist':
      return toMultiPicklistString(ssVal)
    case 'boolean':
      return toBooleanValue(ssVal)
    case 'int':
      return toIntValue(ssVal)
    case 'double':
    case 'percent':
    case 'currency':
      return toNumberValue(ssVal)
    case 'date':
      return toDateOnlyString(ssVal)
    case 'datetime':
      return toDateTimeISO(ssVal)
    case 'reference':
      return toStringValue(ssVal)
    default:
      return ssVal ? String(ssVal) : null
  }
}

const handleOpenAutoFocus = (event: Event) => {
  event.preventDefault()
}

export const ManualSyncDialog: React.FC<ManualSyncDialogProps> = ({
  orgId,
  open,
  from,
  onOpenChange,
  onCreatePursuitSuccess,
}) => {
  const { data: integration, isLoading: isLoadingIntegration } =
    useSalesforceIntegration({ orgId })
  const { data: latestSchema, isLoading: isLoadingLatestSchema } =
    useLatestPursuitSchema({ orgId })
  const { data: pursuitForDefaults } = usePursuit({
    pursuitId: (from.type === 'pursuit' ? from.pursuitId : '') as UUID | '',
  })
  const { data: fields, isLoading: isLoadingFields } =
    useSalesforceOpportunityFields({
      orgId,
      enabled: true,
    })

  const isLoading =
    isLoadingFields || isLoadingLatestSchema || isLoadingIntegration

  const { mutateAsync: createPursuit } = useCreatePursuit({ orgId })
  const { data: latestSchemaId } = useLatestPursuitSchemaId({ orgId })
  const { mutateAsync: createSalesforceOpportunity } =
    useCreateSalesforceOpportunity({ orgId })

  const editableFields: SalesforceField[] = useMemo(() => {
    if (!fields) return []
    return getValidFields(fields.fields)
  }, [fields])

  const defaultValues: ManualSyncDialogFormValues = useMemo(() => {
    if (!fields || !integration?.field_mappings) return {}
    const byLabel = new Map(
      fields.fields.map((f) => [f.label, f] as [string, SalesforceField]),
    )
    const values: ManualSyncDialogFormValues = {}
    for (const mapping of integration.field_mappings) {
      const sf = byLabel.get(mapping.salesforce_field_label)
      if (!sf) continue
      // Only consider fields we actually render
      if (
        !editableFields.find((ef) => ef.name === sf.name) ||
        IGNORE_TYPES.includes(sf.type) ||
        IGNORED_FIELDS.includes(sf.name)
      ) {
        continue
      }

      let v: string | number | boolean | null | undefined = undefined
      // Override takes precedence
      if (
        mapping.override_value !== null &&
        mapping.override_value !== undefined
      ) {
        v = mapping.override_value as unknown as
          | string
          | number
          | boolean
          | null
      } else if (mapping.pursuit_extended_schema_field_id) {
        const key = mapping.pursuit_extended_schema_field_id
        // Prefer direct "from" object data where available
        if (from.type === 'create_pursuit') {
          const cp = from.create_pursuit_req
          switch (key) {
            case 'title':
              v = cp.title
              break
            case 'description':
              v = (cp.description as string | null) ?? null
              break
            case 'due_date': {
              const d = cp.due_date
              v = d ? new Date(d).toISOString() : null
              break
            }
            case 'estimated_value':
              v = cp.estimated_value
              break
            default:
              // dynamic field id
              // @ts-expect-error - extended_data may be loosely typed
              v = cp.extended_data?.[key] ?? undefined
          }
        } else if (from.type === 'pursuit') {
          const p = pursuitForDefaults
          if (p) {
            switch (key) {
              case 'title':
                v = p.title
                break
              case 'description':
                v = (p.description as string | null) ?? null
                break
              case 'due_date': {
                const d = p.due_date
                v = d ? new Date(d).toISOString() : null
                break
              }
              case 'estimated_value':
                v = p.estimated_value
                break
              default:
                // dynamic field id
                // @ts-expect-error - extended_data may be loosely typed
                v = p.extended_data?.[key] ?? undefined
            }
          }
        }
      }

      if (v !== undefined) {
        // Determine SS type for better coercion
        let ssType: ExtendedSchemaFieldType | undefined
        const staticTypes: Record<string, ExtendedSchemaFieldType> = {
          title: 'string',
          description: 'string',
          due_date: 'date',
          estimated_value: 'number',
          stage: 'string',
        }
        if (mapping.pursuit_extended_schema_field_id) {
          const key = mapping.pursuit_extended_schema_field_id
          ssType =
            staticTypes[key] ??
            (latestSchema?.fields?.find(
              (f: ExtendedSchemaDerivedField) => f.id === key,
            )?.type as ExtendedSchemaFieldType | undefined)
        }
        const coerced = coerceToSalesforceValue(sf, v as any, ssType)
        if (coerced !== undefined) {
          values[sf.name] = coerced
        }
      }
    }
    // Fill remaining fields with Salesforce default values
    for (const ef of editableFields) {
      if (values[ef.name] === undefined) {
        values[ef.name] =
          (ef.defaultValue as string | number | boolean | null) ?? null
      }
    }
    return values
  }, [
    fields,
    integration,
    editableFields,
    latestSchema,
    from,
    pursuitForDefaults,
  ])

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ManualSyncDialogFormValues>({ defaultValues })

  const getPrefillSourceForField = useCallback(
    (f: SalesforceField): 'override' | 'pursuit' | undefined => {
      if (!integration?.field_mappings || !fields) return undefined
      const label = fields.fields.find((sf) => sf.name === f.name)?.label || ''
      const m = integration.field_mappings.find(
        (mm) => mm.salesforce_field_label === label,
      )
      if (!m) return undefined
      if (m.override_value !== null && m.override_value !== undefined)
        return 'override'
      if (m.pursuit_extended_schema_field_id) return 'pursuit'
      return undefined
    },
    [integration?.field_mappings, fields],
  )

  const onSubmit = useCallback(
    async (data: ManualSyncDialogFormValues) => {
      try {
        // Coerce outgoing fields to Salesforce types before sending
        const coerced: Record<string, SalesforceValue> = {}
        if (fields) {
          for (const f of getValidFields(fields.fields)) {
            const raw = data[f.name]
            if (raw === undefined) continue
            const v = coerceToSalesforceValue(f, raw as any)
            if (v !== undefined && !(typeof v === 'string' && v.length === 0)) {
              coerced[f.name] = v as any
            }
          }
        }
        switch (from.type) {
          case 'pursuit':
            await createSalesforceOpportunity({
              pursuit_id: from.pursuitId,
              additional_fields: coerced,
            })
            break
          case 'create_pursuit':
            const createdPursuit = await createPursuit({
              ...from.create_pursuit_req,
              extended_schema_id: latestSchemaId ?? null,
              salesforce_options: {
                add_to_salesforce: true,
                additional_fields: coerced,
              },
            })
            onCreatePursuitSuccess?.(createdPursuit)
        }
        toast.success('Synced to Salesforce.')
        onOpenChange(false)
      } catch (error) {
        console.error('Failed to create Salesforce opportunity:', error)
      }
    },
    [
      from.type,
      fields,
      latestSchemaId,
      createSalesforceOpportunity,
      createPursuit,
      onCreatePursuitSuccess,
      onOpenChange,
    ],
  )

  const handleCancel = useCallback(() => {
    reset()
    onOpenChange(false)
  }, [reset, onOpenChange])

  useEffect(() => {
    if (open && !isLoading) {
      reset(defaultValues)
    }
  }, [open, isLoading, defaultValues, reset])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex flex-col w-[720px] max-h-[90vh] p-0 overflow-hidden transition-none focus:outline-none focus:ring-0"
        aria-describedby={undefined}
        onOpenAutoFocus={handleOpenAutoFocus}
        autoFocus={false}
        tabIndex={-1}
      >
        <DialogHeader className="p-6">
          <DialogTitle>
            <div className="flex items-center gap-2">
              <SalesforceIcon size={18} />
              <span>Sync to Salesforce</span>
            </div>
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center items-center h-[400px]">
            <Spinner />
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col flex-1 h-full overflow-y-auto"
          >
            <div className="flex-1 px-6 pb-6">
              <div className="space-y-4">
                {from.type === 'pursuit' && (
                  <FromPursuitHeader pursuitId={from.pursuitId} />
                )}
                {from.type === 'create_pursuit' && (
                  <FromCreatePursuitHeader
                    orgId={orgId}
                    createPursuitReq={from.create_pursuit_req}
                  />
                )}
                {editableFields.length === 0 ? (
                  <p className="text-center text-primary-500 dark:text-primary-400 py-8">
                    No additional fields available for this Salesforce
                    organization.
                  </p>
                ) : (
                  <div className="grid gap-6">
                    {editableFields.map((field) => (
                      <FieldRenderer
                        key={field.name}
                        field={field}
                        control={control}
                        errors={errors}
                        prefillSource={getPrefillSourceForField(field)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="sticky bottom-0 bg-white dark:bg-primary-900 px-6 py-4 border-t border-primary-200 dark:border-primary-800">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm text-red-600 dark:text-red-500">
                  {Object.keys(errors).length > 0 && (
                    <span>
                      Please review and correct the fields with errors.
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline-compact"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary-compact"
                    disabled={isSubmitting || editableFields.length === 0}
                    isLoading={isSubmitting}
                  >
                    Sync to Salesforce
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
